/**
 * Generate a stable suggested patient account number.
 *
 * Format: `ACCT-{LAST}{FF}-{YYYY}-{XXXX}` where:
 *   - LAST: last name uppercased, ASCII letters only, max 8 chars
 *   - FF: first two letters of first name (uppercase, padded with X)
 *   - YYYY: 4-digit DOB year (or current year if DOB missing)
 *   - XXXX: 4-char base36 random suffix for uniqueness
 *
 * Idempotent shape — different patients reliably produce different
 * account numbers; same patient typically generates the same prefix
 * across calls (only the random suffix changes), which is fine for
 * a "suggested" account number that the user can accept or edit.
 *
 * @param {{ first_name?: string, last_name?: string, date_of_birth?: string|null }} patient
 * @returns {string}
 */
export function suggestPatientAccountNumber(patient) {
  const last = String(patient?.last_name || '')
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .slice(0, 8) || 'PATIENT';
  const firstChars = String(patient?.first_name || '')
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .slice(0, 2);
  const ff = (firstChars + 'XX').slice(0, 2);
  const dobYear = String(patient?.date_of_birth || '').slice(0, 4);
  const year = /^\d{4}$/.test(dobYear) ? dobYear : String(new Date().getFullYear());
  const suffix = Math.random().toString(36).slice(2, 6).toUpperCase().padEnd(4, '0');
  return `ACCT-${last}${ff}-${year}-${suffix}`;
}
